const STORAGE_KEY = 'diagnostics-log';
const MAX_EVENTS = 300;
const MAX_MESSAGE_LENGTH = 500;
const MAX_PAYLOAD_LENGTH = 4000;

export default class DiagnosticsService {
    /* @ngInject */
    constructor($window, $document, $http, $q, storageService, configuration) {
        this.$window = $window;
        this.$document = $document;
        this.$http = $http;
        this.$q = $q;
        this.storageService = storageService;
        this.configuration = configuration || {};

        this.events = this.readStoredEvents();
        this.started = false;
        this.consolePatched = false;
        this.originalConsole = {};
    }

    start() {
        if (this.started) {
            return;
        }

        this.started = true;
        this.patchConsole();
        this.log('info', 'diagnostics_started', {
            hasToken: !!this.storageService.getItem('token'),
            host: this.safeString(this.$window.location && this.$window.location.host),
            apiBaseUrl: this.safeString(this.configuration.api)
        });
    }

    canSend() {
        return !!this.getEndpoint();
    }

    log(level, event, payload) {
        const entry = {
            ts: new Date().toISOString(),
            level: this.normalizeLevel(level),
            event: this.safeString(event),
            payload: this.sanitizePayload(payload)
        };

        this.events.push(entry);

        if (this.events.length > MAX_EVENTS) {
            this.events = this.events.slice(this.events.length - MAX_EVENTS);
        }

        this.persist();

        return entry;
    }

    info(event, payload) {
        return this.log('info', event, payload);
    }

    warn(event, payload) {
        return this.log('warn', event, payload);
    }

    error(event, payload) {
        return this.log('error', event, payload);
    }

    send(context) {
        const endpoint = this.getEndpoint();
        const requestId = this.generateRequestId();
        const token = this.storageService.getItem('token');

        if (!endpoint) {
            return this.$q.reject(new Error('Diagnostics endpoint is not configured.'));
        }

        const payload = {
            createdAt: new Date().toISOString(),
            source: this.safeString(context && context.source),
            userId: this.sanitizeUserId(context && context.userId),
            identity: this.sanitizeIdentity(context && context.identity),
            context: this.sanitizePayload(context && context.context),
            app: {
                version: this.safeString(this.configuration.version),
                environment: this.safeString(this.configuration.sentryEnvironment || (this.configuration.production ? 'production' : null)),
                apiBaseUrl: this.safeString(this.configuration.api),
                currentUrl: this.safeString(this.$window.location && this.$window.location.href),
                host: this.safeString(this.$window.location && this.$window.location.host),
                timezone: this.safeString(this.getTimezone()),
                theme: this.getLocalStorageItem('theme'),
                themeResolved: this.getLocalStorageItem('theme_resolved')
            },
            browser: {
                userAgent: this.safeString(this.$window.navigator && this.$window.navigator.userAgent),
                language: this.safeString(this.$window.navigator && this.$window.navigator.language)
            },
            diagnostics: {
                eventCount: this.events.length,
                events: this.events.slice(-MAX_EVENTS)
            }
        };

        this.info('diagnostics_send_attempt', {
            endpoint,
            requestId,
            hasAuthorization: !!token
        });

        const headers = {
            'X-Request-Id': requestId
        };

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        return this.$http.post(endpoint, payload, {
            headers
        }).then((result) => {
            this.info('diagnostics_send_success', {
                endpoint,
                requestId,
                status: result && result.status
            });

            return result;
        }).catch((error) => {
            this.error('diagnostics_send_failed', this.describeHttpError(error, {
                endpoint,
                requestId
            }));

            return this.$q.reject(error);
        });
    }

    describeHttpError(error, extra) {
        const result = Object.assign({}, extra || {});

        if (error && typeof error === 'object') {
            result.status = error.status;
            result.statusText = this.safeString(error.statusText);

            if (error.data && typeof error.data === 'object') {
                result.error = this.safeString(error.data.error);
                result.message = this.safeString(error.data.message);
            } else if (typeof error.data === 'string') {
                result.message = this.truncate(error.data, MAX_MESSAGE_LENGTH);
            } else if (error.message) {
                result.message = this.safeString(error.message);
            }
        } else {
            result.message = this.safeString(error);
        }

        return result;
    }

    patchConsole() {
        if (this.consolePatched || !this.$window.console) {
            return;
        }

        this.consolePatched = true;

        ['log', 'info', 'warn', 'error'].forEach((method) => {
            const original = typeof this.$window.console[method] === 'function'
                ? this.$window.console[method].bind(this.$window.console)
                : null;

            this.originalConsole[method] = original;

            this.$window.console[method] = (...args) => {
                try {
                    this.log(method, `console_${method}`, {
                        args: this.sanitizeConsoleArgs(args)
                    });
                } catch (error) {
                    // never break the app because diagnostics failed
                }

                if (original) {
                    return original(...args);
                }

                return null;
            };
        });
    }

    sanitizeConsoleArgs(args) {
        if (!Array.isArray(args)) {
            return [];
        }

        return args.map((item) => {
            if (item instanceof Error) {
                return {
                    name: this.safeString(item.name),
                    message: this.safeString(item.message),
                    stack: this.truncate(item.stack, MAX_PAYLOAD_LENGTH)
                };
            }

            return this.sanitizePayload(item);
        });
    }

    sanitizePayload(payload) {
        try {
            return this.truncateValue(this.redactSensitiveData(payload), 0);
        } catch (error) {
            return {
                diagnosticsSerializationError: true,
                message: this.safeString(error && error.message)
            };
        }
    }

    redactSensitiveData(value) {
        if (value == null) {
            return value;
        }

        if (typeof value === 'string') {
            return this.truncate(this.maskSensitiveString(value), MAX_PAYLOAD_LENGTH);
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
            return value;
        }

        if (Array.isArray(value)) {
            return value.slice(0, 20).map((item) => this.redactSensitiveData(item));
        }

        if (typeof value === 'object') {
            const result = {};

            Object.keys(value).slice(0, 50).forEach((key) => {
                const normalized = String(key || '').toLowerCase();

                if (this.isSensitiveKey(normalized)) {
                    result[key] = '[REDACTED]';
                } else {
                    result[key] = this.redactSensitiveData(value[key]);
                }
            });

            return result;
        }

        return this.safeString(value);
    }

    isSensitiveKey(key) {
        return [
            'password',
            'token',
            'authorization',
            'cookie',
            'set-cookie',
            'secret'
        ].some((pattern) => key.indexOf(pattern) > -1);
    }

    maskSensitiveString(value) {
        return String(value || '')
            .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/ig, '[REDACTED_EMAIL]')
            .replace(/Bearer\s+[A-Za-z0-9\-_.+/=]+/ig, 'Bearer [REDACTED]');
    }

    sanitizeIdentity(value) {
        if (!value) {
            return null;
        }

        // Identity is intentionally preserved to help support correlate anonymous sign-in issues.
        return this.truncate(String(value), MAX_MESSAGE_LENGTH);
    }

    sanitizeUserId(value) {
        if (value == null || value === '') {
            return null;
        }

        return this.truncate(String(value), MAX_MESSAGE_LENGTH);
    }

    truncateValue(value, depth) {
        if (depth > 4) {
            return '[TRUNCATED_DEPTH]';
        }

        if (typeof value === 'string') {
            return this.truncate(value, MAX_PAYLOAD_LENGTH);
        }

        if (Array.isArray(value)) {
            return value.slice(0, 20).map((item) => this.truncateValue(item, depth + 1));
        }

        if (value && typeof value === 'object') {
            const result = {};

            Object.keys(value).slice(0, 50).forEach((key) => {
                result[key] = this.truncateValue(value[key], depth + 1);
            });

            return result;
        }

        return value;
    }

    persist() {
        try {
            this.storageService.setItem(STORAGE_KEY, this.events);
        } catch (error) {
            // ignore storage errors
        }
    }

    readStoredEvents() {
        try {
            const stored = this.storageService.getItem(STORAGE_KEY);
            return Array.isArray(stored) ? stored.slice(-MAX_EVENTS) : [];
        } catch (error) {
            return [];
        }
    }

    normalizeLevel(level) {
        return ['log', 'info', 'warn', 'error'].indexOf(level) > -1 ? level : 'info';
    }

    safeString(value) {
        if (value == null) {
            return null;
        }

        return this.truncate(String(value), MAX_MESSAGE_LENGTH);
    }

    truncate(value, max) {
        if (typeof value !== 'string') {
            return value;
        }

        return value.length > max
            ? `${value.substr(0, max)}...`
            : value;
    }

    getEndpoint() {
        return this.configuration.supportDiagnosticsEndpoint
            || this.getMonitoringEndpoint()
            || this.configuration.diagnosticsEndpoint
            || null;
    }

    getMonitoringEndpoint() {
        const base = PRODUCTION
            ? this.configuration.monitoringApi || null
            : this.configuration.monitoringApiDev || this.configuration.monitoringApi || null;
        const path = '/support/diagnostics';

        if (!base) {
            return null;
        }

        return `${String(base).replace(/\/+$/, '')}${path}`;
    }

    generateRequestId() {
        const random = Math.random().toString(36).substr(2, 8);
        return `webdiag_${Date.now()}_${random}`;
    }

    getLocalStorageItem(key) {
        try {
            return this.$window.localStorage.getItem(key);
        } catch (error) {
            return null;
        }
    }

    getTimezone() {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
        } catch (error) {
            return null;
        }
    }
}



// WEBPACK FOOTER //
// ./src/common/service/diagnostics.service.js